import React, { useState, useEffect } from 'react';
import Jurenstyles from './Juren.module.css';
import ConfettiPopup from './ConfettiPopup';
const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

const Juren = () => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [isFiveWordCouplet, setIsFiveWordCouplet] = useState(true);
  const [coupletUnits, setCoupletUnits] = useState(Array(10).fill(''));
  const [submittedCouplet, setSubmittedCouplet] = useState('');
  const [assessmentResult, setAssessmentResult] = useState('');
  const [modelAssessment, setModelAssessment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentLevel, setCurrentLevel] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showBadgePrompt, setShowBadgePrompt] = useState(false);
  const [hasJurenBadge, setHasJurenBadge] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [placeholderText, setPlaceholderText] = useState('');
  const [pingZeFeedback, setPingZeFeedback] = useState(''); // New state for Ping/Ze feedback
  const placeholderContent = 'Type here...';

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);

    if (token) {
      fetchUserLevel(token);
    }
  }, []);
  
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchSubmissions(token);
    }
  }, [])

  useEffect(() => {
    if (!coupletUnits[0]) {
      let index = 0;
      const interval = setInterval(() => {
        setPlaceholderText(placeholderContent.substring(0, index));
        index = (index + 1) % (placeholderContent.length + 1);
      }, 200);
      return () => clearInterval(interval);
    }
  }, [coupletUnits]);

  const handleToggle = () => {
    setIsFiveWordCouplet(!isFiveWordCouplet);
    setCoupletUnits(Array(!isFiveWordCouplet ? 10 : 14).fill(''));
  };

  const handleInputChange = (index, value) => {
    const newCoupletUnits = [...coupletUnits];
    newCoupletUnits[index] = value;
    setCoupletUnits(newCoupletUnits);
  };

  const handleSubmission = async (type) => {
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      let couplet;
      if (submittedCouplet) {
        couplet = submittedCouplet;
      } else {
        const line1 = coupletUnits.slice(0, isFiveWordCouplet ? 5 : 7).join(' ');
        const line2 = coupletUnits.slice(isFiveWordCouplet ? 5 : 7).join(' ');
        couplet = `${line1}\n${line2}`;
      }
  
      const endpoint = type === 'manual' ? 'manual-couplet-submission' : 'assess-couplet';
      const payload = {
        couplet: couplet,
        level: "Juren",
        user_level: currentLevel,
      };
  
      const token = localStorage.getItem('token');
  
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }), // Only include the Authorization header if token exists
      };
  
      const response = await fetch(`${API_URL}/${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
  
      const data = await response.json();
  
      if (response.ok && data.is_valid) {
        if (type === 'manual') {
          setSuccessMessage(data.message || 'Couplet submitted for manual review.');
        } else {
          setAssessmentResult(data.model_assessment);
          setPingZeFeedback(data.ping_ze_feedback || ''); // Display AI feedback
  
          // Show badge prompt only for logged-in users who don't already have the Juren badge
          if (isLoggedIn && !hasJurenBadge) {
            setShowBadgePrompt(true);
          }
        }
      } else {
        setErrorMessage(data.message || 'Failed to submit couplet.');
        setAssessmentResult('');
      }
    } catch (error) {
      console.error('Error submitting couplet:', error);
      setErrorMessage('Error submitting couplet.');
    }
    setIsLoading(false);
  };
  

  const fetchUserLevel = async (token) => {
    try {
      const response = await fetch(`${API_URL}/user/current-level`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setCurrentLevel(data.level);

      if (data.achievements && data.achievements.Juren) {
        setHasJurenBadge(true);
      } else {
        setHasJurenBadge(false);
      }
    } catch (error) {
      console.error('Error fetching user level:', error);
    }
  };
  
  const handleBadgeSubmission = async (passed) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found. Please log in again.');
      }
  
      if (passed) {
        // Only make the API call if the user selects "Yes"
        const response = await fetch(`${API_URL}/user/achievements/juren`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ level: 'Juren' }),
        });
  
        if (response.ok) {
          setShowConfetti(true);
          setCurrentLevel('Juren');
          setHasJurenBadge(true);
          setTimeout(() => {
            closeConfetti();
          }, 10000); // Reload to reflect the new achievement status
        } else {
          console.error('Failed to update user level.');
        }
      } else {
        // If the user selects "No," display a try again message
        setErrorMessage('Try again and make your couplet better based on the assessment feedback.');
      }
    } catch (error) {
      console.error('Error updating user level:', error);
    }
    // Hide the badge prompt regardless of the user’s choice
    setShowBadgePrompt(false);
  };
  
  const closeConfetti = () => {
    setShowConfetti(false); // Hide the confetti
    window.location.reload(); // Reload the page immediately
  };

  const renderBadgePrompt = () => (
    isLoggedIn && (
      <div className={Jurenstyles.badgePrompt}>
        <h4>Did you pass the Juren level?</h4>
        <button className={Jurenstyles.button} onClick={() => handleBadgeSubmission(true)}>Yes</button>
        <button className={Jurenstyles.button} onClick={() => handleBadgeSubmission(false)}>No</button>
      </div>
    )
  );
  

  const fetchSubmissions = async (token) => {
    try {
      const response = await fetch(`${API_URL}/submissions/?level=Juren`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();

      if (Array.isArray(data)) {
        setSubmissions(data);
      } else {
        setSubmissions([]);
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
      setSubmissions([]);
    }
  };

  const [isZeStart, setIsZeStart] = useState(true);

  const renderCoupletInputs = () => {
    const lineUnits = isFiveWordCouplet ? 5 : 7;
    const totalLines = 2;
  
    // Tone pattern definitions
    const fiveWordZeStart = [
      '○/●', '○', '○/●', '●', '○',
      '○/●', '●', '○/●', '○', '○'
    ];
  
    const fiveWordPingStart = [
      '○/●', '●', '○/●', '○', '○',
      '○/●', '○', '○/●', '●', '○'
    ];
  
    const sevenWordZeStart = [
      '○/●', '○', '○/●', '●', '○/●', '○', '●',
      '○/●', '●', '○/●', '○', '○/●', '●', '●'
    ];
  
    const sevenWordPingStart = [
      '○/●', '●', '○/●', '○', '○/●', '●', '○',
      '○/●', '○', '○/●', '●', '○/●', '○', '○'
    ];
  
    // Select correct pattern
    const patternArray = isFiveWordCouplet
      ? (isZeStart ? fiveWordZeStart : fiveWordPingStart)
      : (isZeStart ? sevenWordZeStart : sevenWordPingStart);
  
    const lineBreaks = [];
  
    for (let i = 0; i < totalLines; i++) {
      const units = [];
      for (let j = 0; j < lineUnits; j++) {
        const index = i * lineUnits + j;
        units.push(
          <div key={`unit-${i}-${j}`} className={Jurenstyles.unitContainer}>
  <div className={Jurenstyles.symbolWrapper}>
    {patternArray[index]?.includes('/') ? (
      <>
        <div className={Jurenstyles.toneCircleRow}>
          {patternArray[index].split('/').map((tone, i) => (
            <div
              key={i}
              className={`${Jurenstyles.toneCircle} ${tone === '●' ? Jurenstyles.ze : Jurenstyles.ping}`}
            ></div>
          ))}
        </div>
        <div className={Jurenstyles.toneLabel}>Ping / Ze</div>
      </>
    ) : (
      <>
        <div
          className={`${Jurenstyles.toneCircle} ${patternArray[index] === '●' ? Jurenstyles.ze : Jurenstyles.ping}`}
        ></div>
        <div className={Jurenstyles.toneLabel}>
          {patternArray[index] === '●' ? 'Ze' : 'Ping'}
        </div>
      </>
    )}
  </div>

  <input
    type="text"
    value={coupletUnits[index]}
    onChange={(e) => handleInputChange(index, e.target.value)}
    className={Jurenstyles.unitBox}
    placeholder={index === 0 ? placeholderText : ''}
    onFocus={() => index === 0 && setPlaceholderText('')}
  />
</div>


        );
  
        // Insert gaps for spacing
        if (isFiveWordCouplet && j === 1) {
          units.push(<div key={`gap-${i}-${j}`} className={Jurenstyles.gap}></div>);
        } else if (!isFiveWordCouplet && (j === 1 || j === 3)) {
          units.push(<div key={`gap-${i}-${j}`} className={Jurenstyles.gap}></div>);
        }
      }
  
      lineBreaks.push(
        <div key={`line-${i}`} className={Jurenstyles.line}>
          {units}
        </div>
      );
    }
  
    return <div className={Jurenstyles.unitInputs}>{lineBreaks}</div>;
  };
  

  const openSubmissionsModal = () => {
    setIsModalOpen(true);
  };

  const handleReadMore = (submission) => {
    setSelectedSubmission(submission);
  };

  const closeReadMoreModal = () => {
    setSelectedSubmission(null);
  };

  const renderCoupletWithLineBreaks = (couplet) => {
    const lines = couplet.split('\n');
    return lines.map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index !== lines.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  // Render "Read More" modal
  const renderReadMoreModal = () => (
    selectedSubmission && (
      <div className={`${Jurenstyles.modal} ${Jurenstyles.modalOpen}`} style={{ zIndex: 1100 }}>
        <div className={Jurenstyles.readMoreModalContent}>
          <span className={Jurenstyles.closeButton} onClick={closeReadMoreModal}>&times;</span>
          <h3>Submission Details</h3>
          <p className={Jurenstyles.couplet}><strong>Couplet:</strong> {renderCoupletWithLineBreaks(selectedSubmission.couplet)}</p>
          <p className={Jurenstyles.feedback}><strong>Feedback:</strong> {selectedSubmission.submission_type === 'ai' ? selectedSubmission.model_assessment : selectedSubmission.feedback || 'No feedback available'}</p>
          <p className={Jurenstyles.type}><strong>Type:</strong> {selectedSubmission.submission_type}</p>
          <p className={Jurenstyles.status}><strong>Status:</strong> {selectedSubmission.status}</p>
          <p className={Jurenstyles.submittedAt}><strong>Submitted At:</strong> {new Date(selectedSubmission.submitted_at).toLocaleString()}</p>
        </div>
      </div>
    )
  );

  const renderManualAssessmentPrompt = () => (
    isLoggedIn && (
      <div className={Jurenstyles.manualAssessmentContainer}>
        <p>If you want a manual assessment for more accuracy or if you disagree with the model, you can make a manual submission and receive feedback after a few days or weeks by the admin (check your submissions to see the feedback).</p>
        <button
          onClick={() => handleSubmission('manual')}
          disabled={isLoading}
          className={Jurenstyles.manualButton}
          title="Your submission will be sent for manual review."
        >
          Submit for Manual Assessment
        </button>
      </div>
    )
  );
  
  
  const renderSubmissionContainer = () => (
    <div className={Jurenstyles.submissionContainer}>
      <h2>Welcome to your Juren Exam!</h2>
      <div className={Jurenstyles.toggleGroup}>
        <div className={Jurenstyles.toggleRow}>
            <label className={Jurenstyles.toggleLabel}>{isFiveWordCouplet ? '5-word couplet' : '7-word couplet'}</label>
            <label className={Jurenstyles.switch}>
              <input
                type="checkbox"
                checked={!isFiveWordCouplet}
                onChange={handleToggle}
              />
              <span className={`${Jurenstyles.slider} ${Jurenstyles.round}`}></span>
            </label>
          </div>
        <div className={Jurenstyles.toggleRow}>
          <label className={Jurenstyles.toggleLabel}>{isZeStart ? 'Ze Start' : 'Ping Start'}</label>
          <label className={Jurenstyles.switch}>
            <input
              type="checkbox"
              checked={!isZeStart}
              onChange={() => setIsZeStart(!isZeStart)}
            />
            <span className={`${Jurenstyles.slider} ${Jurenstyles.round}`}></span>
          </label>
        </div>
      </div>

      <p>Type in unit by unit:</p>
      {renderCoupletInputs()}
      <p>OR</p>
      <textarea
        placeholder={"Enter your couplet here...\n\nNote: Please remove all punctuation from your couplet including apostrophes!"}
        value={submittedCouplet}
        onChange={(e) => setSubmittedCouplet(e.target.value)}
        className={Jurenstyles.textArea}
      />
      <div className={Jurenstyles.submitButtonsContainer}>
        <button
          onClick={() => handleSubmission('ai')}
          disabled={isLoading}
          className={`${Jurenstyles.aiButton} ${Jurenstyles.button}`}
          title="You will receive an instant AI assessment."
        >
          Submit for AI Assessment
        </button>
      </div>
      {isLoading && <div className={Jurenstyles.loader}></div>}
      {errorMessage && <div className={Jurenstyles.errorMessage}>{errorMessage}</div>}
      {successMessage && <div className={Jurenstyles.successMessage}>{successMessage}</div>}
  
      {/* Display the assessment result */}
      {assessmentResult && (
        <div className={Jurenstyles.assessmentResult}>
          {assessmentResult}
        </div>
      )}
  
      {/* Display the Model Assessment */}
      {modelAssessment && (
        <div className={Jurenstyles.modelAssessment}>
          <h4>Model Assessment</h4>
          <p className={Jurenstyles.generatedText}>{modelAssessment}</p>
        </div>
      )}
  
      {/* Display Ping/Ze Feedback */}
      {pingZeFeedback && (
        <div className={Jurenstyles.pingZeFeedback}>
          <h4>Ping/Ze Pattern Feedback</h4>
          <p>{pingZeFeedback}</p>
        </div>
      )}
  
      {/* Display the badge prompt only if user hasn't earned it */}
      {!hasJurenBadge && showBadgePrompt && renderBadgePrompt()}
  
      {/* Display the manual assessment prompt only if there is an assessment result */}
      {assessmentResult && renderManualAssessmentPrompt()}
    </div>
  );
  

  // Modal for showing submissions
  const renderSubmissionsModal = () => (
    <div className={`${Jurenstyles.modal} ${isModalOpen ? Jurenstyles.modalOpen : ''}`}>
      <div className={Jurenstyles.modalContent}>
        <span className={Jurenstyles.closeButton} onClick={() => setIsModalOpen(false)}>&times;</span>
        <h3>Your Juren Submissions</h3>
        {Array.isArray(submissions) && submissions.length === 0 ? (
          <p>No submissions yet.</p>
        ) : (
          <div className={Jurenstyles.submissionsContainer}>
            {submissions.map((submission, index) => (
              <div key={index} className={Jurenstyles.submissionItem}>
                <p><strong>Couplet:</strong> {renderCoupletWithLineBreaks(submission.couplet)}</p>
                <p>
                  <strong>Feedback:</strong>
                  {submission.submission_type === 'manual' && submission.status === 'finished' ? (
                    submission.feedback || 'No feedback available'
                  ) : submission.submission_type === 'manual' && submission.status !== 'finished' ? (
                    <span className={Jurenstyles.pending}>Pending</span>
                  ) : submission.model_assessment && submission.model_assessment.length > 100 ? (
                    <>
                      {submission.model_assessment.substring(0, 100)}...
                      <span className={Jurenstyles.readMore} onClick={() => handleReadMore(submission)}> Read More</span>
                    </>
                  ) : (
                    submission.model_assessment || 'No feedback available'
                  )}
                </p>
                <p><strong>Type:</strong> {submission.submission_type}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={Jurenstyles.container}>
      {showConfetti && (
        <ConfettiPopup
          message="Congratulations! You now have a Juren badge!"
          onClose={closeConfetti}
        />
      )}
      <button onClick={() => setIsModalOpen(true)} className={`${Jurenstyles.openModalButton} ${Jurenstyles.button}`}>
        View Previous Submissions
      </button>
      {renderSubmissionContainer()}
      {renderSubmissionsModal()}
      {renderReadMoreModal()}
    </div>
  );
};

export default Juren;
